import React, { useState, useEffect, useRef } from 'react';
import MultitrackPianoRoll from './viz/MultitrackPianoRoll';
import { pauseOtherAudio } from './viz/pianoRollShared';
import { LAKH_COMPARISONS } from './data/lakhExamples';

const ModelCard = ({ model }) => {
  const audioRef = useRef(null);

  const cardClass = [
    'model-audio-player',
    model.isOurs ? 'model-card-ours' : '',
    model.isBaseline ? 'model-card-baseline' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClass}>
      <div className="model-header">
        <h4 className={`model-name ${model.name === 'MIDI-LLM' ? 'model-name-midi-llm' : ''}`}>
          {model.name}
          {model.isOurs && <span className="ours-badge">Ours</span>}
        </h4>
        <p className="model-description">{model.description}</p>
      </div>

      <MultitrackPianoRoll jsonUrl={model.notesUrl} audioRef={audioRef} />

      <div className="audio-player-container">
        <audio
          ref={audioRef}
          controls
          className="simple-audio-player"
          preload="metadata"
          src={model.audioUrl}
          onPlay={pauseOtherAudio}
        >
          Your browser does not support the audio element.
        </audio>
      </div>
    </div>
  );
};

const ComparisonSelector = ({ comparisons, currentId, onSelect }) => (
  <div className="comparison-selector">
    <h4 className="selector-title">Select Example:</h4>
    <div className="comparison-chips">
      {comparisons.map((comparison) => (
        <button
          key={comparison.id}
          className={`comparison-chip ${currentId === comparison.id ? 'selected' : ''}`}
          onClick={() => onSelect(comparison.id)}
        >
          {comparison.label}
        </button>
      ))}
    </div>
  </div>
);

const MultitrackExamples = ({ onReturnToLive }) => {
  const [currentComparisonId, setCurrentComparisonId] = useState(1);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);

  const currentComparison = LAKH_COMPARISONS.find(
    (comp) => comp.id === currentComparisonId
  );

  useEffect(() => {
    if (!currentComparison?.promptFile) return undefined;

    let cancelled = false;
    setIsLoadingPrompt(true);

    fetch(currentComparison.promptFile)
      .then((response) => response.text())
      .then((text) => {
        if (cancelled) return;
        setCurrentPrompt(text.replace(/\s*\n+\s*/g, ' ').trim());
        setIsLoadingPrompt(false);
      })
      .catch(() => {
        if (cancelled) return;
        setCurrentPrompt('Error loading prompt');
        setIsLoadingPrompt(false);
      });

    return () => { cancelled = true; };
  }, [currentComparison]);

  return (
    <div className="static-demo-section">
      <div className="static-demo-header">
        <p>
          These prompts are sampled from a held-out split of the <strong>MidiCaps</strong> dataset (<a href="https://arxiv.org/abs/2406.02255" target="_blank" rel="noopener noreferrer" className="dataset-link">Melechovsky et al. 2024</a>).
          We compare our <span style={{ fontFamily: 'Playfair Display', fontWeight: 'bold' }}>MIDI-LLM</span> to Text2midi (<a href="https://arxiv.org/abs/2412.16526" target="_blank" rel="noopener noreferrer" className="dataset-link">Bhandari et al. 2025</a>).
          Each piano roll is colored by General MIDI instrument family; use the pills to isolate parts, and the playhead follows the audio.
        </p>
      </div>

      <ComparisonSelector
        comparisons={LAKH_COMPARISONS}
        currentId={currentComparisonId}
        onSelect={setCurrentComparisonId}
      />

      {currentComparison && (
        <div className="comparison-display">
          <div className="prompt-display">
            <label className="prompt-label">Input Prompt:</label>
            <div className="prompt-box">
              {isLoadingPrompt ? 'Loading prompt...' : currentPrompt}
            </div>
          </div>

          <div className="audio-comparison">
            <div className="comparison-row">
              {currentComparison.models.map((model) => (
                // Keying on the comparison forces a fresh mount per example, so
                // no stale playhead or in-flight fetch survives the switch.
                <ModelCard
                  key={`${currentComparison.id}-${model.key}`}
                  model={model}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="static-demo-footer">
        <p className="demo-note">
          💡 Since both models were trained on segments of songs (rather than full songs), any "duration" specified in the prompt likely takes no effect.
        </p>
        {onReturnToLive && (
          <button
            className="return-to-live-btn-large"
            onClick={onReturnToLive}
          >
            🪗 Go Play with Live Demo !!
          </button>
        )}
      </div>
    </div>
  );
};

export default MultitrackExamples;
